<script setup lang="ts">
import { computed, onBeforeMount, onMounted, ref } from 'vue'
import utilModelsService from '@/services/util.models.service'
import { useAuthStore } from '@/stores/auth.store'
import closeIcon from '@/assets/images/svg/AuthOnboarding/CloseButton.svg'
import BaseInput from '@/components/forms/BaseInput.vue'
import SocialLogin from '@/components/header/auth/SocialLoginComp.vue'
import AuthOnboardingLabel from '@/components/header/auth/AuthOnboardingLabel.vue'
import LoginRegisterBanner from '@/components/header/auth/AuthOnboardingAdvBanner/LoginRegisterBanner.vue'
import Button from '@/components/atoms/button/index.vue'
import ModalLayoutAuth from '@/layouts/ModelLayoutAuth.vue'
import recaptchaService from '@/services/recaptcha.service'
import authService, { type IloginRequest } from '@/services/auth.service'
import { useSubscribeStore } from '@/stores/subscribe.store'
import { AUTHMODELHS } from '@/utils/models'
import { until } from '@vueuse/core'
import { useField, useForm } from 'vee-validate'
import { VueRecaptcha } from 'vue-recaptcha'
import { string } from 'yup'
import { RECAPTCHA_KEYS } from '@/utils/recaptcha'
import { useRoute, useRouter } from 'vue-router'
import { handleError } from '@/services/errorHandler.service'
import { delay } from 'lodash-es'

const colorTheme = ref<string>('light')
const router = useRouter()
const authStore = useAuthStore()
const route = useRoute()

const { errors, handleSubmit, resetForm } = useForm()

const { value: email, errorMessage: emailError } = useField('username', string().required().email())
const { value: password, errorMessage: passwordError } = useField(
  'password',
  string().required().min(5)
)

const sitekey = ref<string | null>(RECAPTCHA_KEYS.RECAPTCHALOGINSITEKEY)
const rechaptha = ref<any>(null)
const recapthaToken = ref<string>('')
const recapthaError = ref<string>('')
const showRecaptcha = ref<boolean>(false)
const inputTypePassword = ref<string>('password')
const isloading = ref<boolean>(false)

const httpError = ref<string>('')

const togglePasswordEyeIcon = ref<boolean>(false)

const iconsToShow = computed(() => {
  if (togglePasswordEyeIcon.value) {
    return 'fa-regular fa-eye'
  } else {
    return 'fa-regular fa-eye-slash'
  }
})

const canSubmit = computed(() => {
  return Object.keys(errors.value).length === 0 && !isloading.value
})

/**
 * Handles the response from the reCAPTCHA verification and sets the token.
 * @param response - The reCAPTCHA response token.
 */
const recaptchaVerified = (response: string) => {
  recapthaToken.value = response
  recapthaError.value = ''
}

/**
 * Handles the reCAPTCHA error and sets the error message.
 * @param error - The reCAPTCHA error message.
 */
const recaptchaError = (error: string) => {
  recapthaToken.value = ''
  recapthaError.value = error
  rechaptha.value.reset()
}

const onSubmit = handleSubmit(async (values) => {
  rechaptha.value.execute()

  await until(recapthaToken).changed()

  if (!recapthaToken.value || recapthaError.value) {
    return
  }

  const requestPayload: IloginRequest = {
    username: values.username,
    password: values.password,
    recaptcha: recapthaToken.value
  }

  isloading.value = true
  httpError.value = 'Logging you in. please wait...'
  await authService
    .login(requestPayload)
    .then(async () => {
      httpError.value = ''
      resetForm()

      if (useAuthStore().awaitRedirectTo) {
        const redirectTo: string = useAuthStore().redirectTo as string
        useAuthStore().setRedirectTo(null)

        await authService.initAuth(true)
        await utilModelsService.closeModalAsync(AUTHMODELHS.LOGIN).then(async () => {
          router.push(redirectTo)
        })
      } else if (useSubscribeStore().redirectTo) {
        await authService.initAuth(true)
        await utilModelsService.closeModalAsync(AUTHMODELHS.LOGIN).then(() => {
          router.push(useSubscribeStore().redirectTo)
        })
      } else {
        // window.location.reload()
        await utilModelsService.closeModalAsync(AUTHMODELHS.LOGIN).then(async () => {
          await authService.initAuth(true)
          // if (authStore.onboarderCompleted && !ideStore.isDefaultIde && !ideStore.isIdeView) {
          //   utilModelsService.openModal(AUTHMODELHS.SWITCHINGACCOUNT)
          // }
        })
      }
    })
    .catch((error: any) => {
      httpError.value = handleError(error)
      delay(() => {
        authService.openOtpModelAgain(handleError(error), values.username)
      }, 2000)
    })
    .finally(() => {
      recapthaToken.value = ''
      rechaptha.value.reset()
      isloading.value = false
    })
})

/**
 * If prop is true, page retrieves data again from API after changes from modal
 */
const handleLoadDataWhenClose = () => {
  authStore.setRedirectTo(null)
}

/**
 *  Toggle to show and hide password
 */
const handleShowPassword = () => {
  if (inputTypePassword.value === 'password') {
    inputTypePassword.value = 'text'
    togglePasswordEyeIcon.value = true
  } else {
    inputTypePassword.value = 'password'
    togglePasswordEyeIcon.value = false
  }
}
/**
 * Redirect to forgot password modal
 */
const forgotPasswordRedirect = () => {
  authStore.setOtpRedirectFrom('Login')
  utilModelsService.openModal(AUTHMODELHS.FORGOTPASSWORD)
}

onBeforeMount(async () => {
  sitekey.value = await recaptchaService.getRechapthaKey(RECAPTCHA_KEYS.RECAPTCHALOGINSITEKEY)
})
onMounted(async () => {
  await utilModelsService.onOpenModel((hs: string | null) => {
    if (hs === AUTHMODELHS.LOGIN && !showRecaptcha.value) {
      showRecaptcha.value = true
    }
  })

  // Reset form when overlay modal is closed
  const el = document.getElementById(AUTHMODELHS.LOGIN)
  if (el) {
    el.addEventListener('close.hs.overlay', () => {
      resetForm()
    })
  }
})
</script>

<template>
  <ModalLayoutAuth :hs="AUTHMODELHS.LOGIN">
    <div class="flex h-screen w-full flex-col">
      <div class="absolute right-0 flex justify-end">
        <img
          :src="closeIcon"
          alt="Close"
          class="link-primary hs-collapse-toggle mr-5 mt-2 h-[20px] w-[20px] cursor-pointer transition-all"
          :data-hs-overlay="`#${AUTHMODELHS.LOGIN}`"
          @click="handleLoadDataWhenClose"
          v-show="!(route.name === 'extensionLogin' && !route.query.register)"
        />
      </div>
      <div class="flex w-full flex-1 p-1 sm:p-5">
        <div class="hidden w-full rounded-3xl bg-[#EBEFF6] md:hidden lg:block lg:min-w-[45%]">
          <LoginRegisterBanner
            text="A feature-rich coding suite powered by AI"
            highlighertext="powered by AI"
          />
        </div>

        <div class="mt-10 h-full w-full rounded-md p-5 md:mt-5 lg:min-w-[50%]">
          <div class="flex items-center justify-center">
            <AuthOnboardingLabel
              title="Sign in to JDoodle"
              customstyle="mt-5
              text-sm font-normal"
              customstyleTitle="text-center text-3xl font-medium leading-10"
            />
          </div>

          <div class="mt-4 flex w-full flex-col sm:mt-5">
            <!-- Social Login -->
            <div class="flex justify-center">
              <SocialLogin />
            </div>
            <div
              class="p-xsmall flex items-center p-4 py-4 text-sm text-text-tertiary before:mr-6 before:flex-[1_1_0%] before:border before:border-neutral-100 after:ml-6 after:flex-[1_1_0%] after:border after:border-neutral-100"
            >
              or
            </div>
          </div>
          <div class="p-5">
            <!-- form -->
            <form @submit.prevent="onSubmit">
              <div class="flex w-full flex-col items-center justify-center">
                <div class="w-full lg:w-[500px]">
                  <BaseInput
                    id="login-email"
                    label="Email Address"
                    :inputType="'email'"
                    placeholder="ilovecoding@jdoodle.com"
                    autocomplete="email"
                    :error="emailError"
                    v-model="email"
                    :isBoldLabel="true"
                    :isLightMode="true"
                    :isLightGray="false"
                  />
                  <BaseInput
                    id="login_pwd"
                    label="Password"
                    :inputType="inputTypePassword"
                    placeholder="*****"
                    autocomplete="current-password"
                    :error="passwordError"
                    v-model="password"
                    :isBoldLabel="true"
                    :isPassword="true"
                    :isPasswordIcon="iconsToShow"
                    @click="handleShowPassword"
                    :isLightMode="true"
                    :isLightGray="false"
                    :isEnterKeySupport="true"
                  />

                  <div class="text-secondary text-right text-base">
                    <Button
                      variant="link"
                      type="button"
                      class="p-0 text-xs font-normal"
                      @click="forgotPasswordRedirect"
                    >
                      Forgot Password ?</Button
                    >
                  </div>

                  <p :class="['p-small', 'error', { hidden: !recapthaError }]">
                    {{ recapthaError }}
                  </p>

                  <p
                    :class="[
                      'p-small ',
                      'text-center',
                      { hidden: !httpError, 'text-sky-950': isloading, error: !isloading }
                    ]"
                  >
                    {{ httpError }}
                  </p>
                </div>
              </div>

              <vue-recaptcha
                v-if="showRecaptcha"
                size="invisible"
                ref="rechaptha"
                :theme="colorTheme === 'dark' ? 'dark' : 'light'"
                :sitekey="sitekey || ''"
                @verify="recaptchaVerified"
                @fail="recaptchaError('Recaptcha failed. Please verify again.')"
                @error="recaptchaError"
              />

              <div class="flex justify-center sm:mt-9">
                <Button
                  :disabled="!canSubmit"
                  color="secondary"
                  variant="filled"
                  size="medium"
                  type="submit"
                  class="mt-6 h-[48px] sm:w-[171px]"
                >
                  Sign In
                </Button>
              </div>
            </form>

            <div class="mt-1 text-center text-sm font-medium leading-tight">
              <span> New to JDoodle?</span>
              <Button
                type="button"
                color="secondary"
                variant="link"
                class="ml-1 mt-2 p-0 font-semibold"
                :data-hs-overlay="`#${AUTHMODELHS.REGISTER}`"
              >
                Sign Up
              </Button>
            </div>

            <!-- Terms and Condition -->
            <div class="mt-9 flex justify-center text-sm">
              <p class="w-[232px] sm:w-auto">
                <span class="text-xs font-normal leading-normal">
                  By signing in, you accept our
                </span>

                <router-link
                  to="/terms"
                  target="_blank"
                  class="text-xs font-normal leading-normal text-new-secondary-600 underline"
                  >Terms & Conditions</router-link
                >
                and
                <router-link
                  to="/privacy"
                  target="_blank"
                  class="text-xs font-normal leading-normal text-new-secondary-600 underline"
                  >Privacy Policy</router-link
                >
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ModalLayoutAuth>
</template>
